import { ApiError } from '@/lib/api/client';
import { assertPromotionQuote, promotionCartMatches, promotionItems, promotionLineKey, readCartPromotion, remainingPromotion, saveCartPromotion, type SavedCartPromotion, type CartPromotionLine } from '@/features/marketplace/cartPromotion';
import { useCartPromotionQuote } from '@/lib/api/hooks';
import { PlanSelect } from "@/design-system/primitives/PlanSelect";
import { useLabsExperiment, useLabView } from '@/features/labs/labExperiment';
import { useLabCartPurchase, readLabCartAttempt } from '@/features/labs/useLabCartPurchase';
import { LabPurchaseOptions, labIntervalLabel } from '@/features/labs/LabPurchaseOptions';
import { useLabPreview } from '@/features/labs/labPreview';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { capturePaymentStep, paymentStepKey } from '@/lib/api/cartContext';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';


import {
  cart,
  FREQUENCY_CYCLE_LABELS,
  FREQUENCY_LABELS,
  isCourseItem,
  offerableFrequencies,
  renewalCentsFor,
  useCart,
  type CartItem,
  type PlanFrequency,
} from '@/features/marketplace/stores/cart';
import {
  promoDiscountCents,
  promoErrorCopy,
  useAccountCredit,
  useLifecycleEvent,
  useMarketplacePurchase,
  usePaymentMethods,
  useValidatePromoCode,
  type PromoCodeValidation,
} from '@/lib/api/hooks';
import type { PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';
import { applePaySheet, useApplePaySheet } from '@/features/marketplace/applePaySheet';
import { PaymentMethodPicker } from '@/features/marketplace/components/PaymentMethodPicker';
import { Icon as BrandIcon } from '@/lib/Icon';
import { track } from '@/lib/analytics';
import { cn } from '@/lib/cn';
import { postPurchaseDestination } from '@/features/marketplace/postPurchaseIntake';
import { marketplaceAfterPurchase } from '@/features/marketplace/postPurchaseMarketplace';
import type { MarketplaceResponse } from '@/types/api';
import { openPurchaseRequest, purchaseRequest, recordPaidLines, paidPurchaseLines, resumableLines, retirePurchaseRequest, isClosedPurchaseAttempt, isUpgradeDoseItem, purchaseDoseSelection } from '@/features/marketplace/purchaseRequest';
import { CarePurchaseNote } from '@/features/onboarding/CarePurchaseNote';
import {
  readSupplementAttempt,
  useSupplementCartPurchase,
} from '@/features/marketplace/supplements/useSupplementCartPurchase';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useSupplementCatalog } from '@/features/marketplace/supplements/api';
import { isPurchasableSupplement } from '@/features/marketplace/supplements/catalog';
import { useLabCatalog } from '@/lib/api/labHooks';
import { courseIntakeLines, readCourseAttempt, useCourseCartPurchase, type CoursePromoLine } from '@/features/marketplace/useCourseCartPurchase';
import { memberPriceCents, useMembership } from '@/lib/membership';

/**
 * CartDrawer — single source-of-truth cart panel for the app. Open state
 * lives in the `cartUi` store, so any surface (SearchBar's cart icon,
 * Marketplace's "Add to Stack" sticky button, deep-linked `?view=cart`)
 * just calls `cartUi.open()`.
 *
 * Two states:
 *   Empty: 'Cart empty' + 'Shop' CTA → /app/shop
 *   Filled: 'PLAN [N]' header + item rows (thumb + title + subtitle +
 *     price + per-row Remove) + sticky bottom Subtotal + Confirm Purchase.
 *
 * Confirm Purchase uses the selected saved card for one-time supplements and
 * compound subscriptions in this drawer. Paid lines leave the cart while failed
 * lines remain. Bank verification and uncertain supplement attempts retain their
 * identity; Web Locks serialize confirms across tabs. Required compound intakes
 * keep their existing destination; supplement purchases surface in Orders.
 */

function formatPrice(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 ? 2 : 0,
  }).format(cents / 100);
}

function labBillingCopy(item: CartItem, quote?: CartPromotionLine) {
  const today = quote?.total_cents ?? item.price_cents;
  if (item.lab_purchase_type === 'one_time') return `One test. One payment of ${formatPrice(today)}.`;
  const renewal = quote?.renewal_amount_cents ?? item.price_cents;
  const discountWindow = quote?.discount_duration === 'repeating' && quote.discount_duration_months
    ? ` The discount applies for the first ${quote.discount_duration_months} months, then renewals are ${formatPrice(item.price_cents)}.` : '';
  return `${formatPrice(today)} today, then ${formatPrice(renewal)} every ${labIntervalLabel(item.lab_interval_months)}.${discountWindow} One test per charge. Manage or cancel future renewals in Account.`;
}

export function CartDrawer({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { session } = useAuth();
  const userId = session?.user.id;
  const currentUser = useRef(userId);
  const paymentCapture = useRef<AbortController | null>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  currentUser.current = userId;
  useEffect(() => {
    if (!open) return;
    // Product details unmount before this frame; continue keyboard navigation
    // in the cart without moving the page behind it.
    const frame = requestAnimationFrame(() => closeButton.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [open]);
  const [savedPromotion, setSavedPromotion] = useState<{ owner?: string; value: SavedCartPromotion | null }>(() => ({ owner: userId, value: readCartPromotion(userId) }));
  const cartPromotion = savedPromotion.owner === userId ? savedPromotion.value : null;
  const [paymentSelection, setPaymentSelection] = useState<{ owner?: string; id: string | null }>({ owner: userId, id: null });
  const selectedPmId = cartPromotion?.started ? cartPromotion.paymentMethodId : paymentSelection.owner === userId ? paymentSelection.id : null;
  const selectedPmRef = useRef(selectedPmId);
  selectedPmRef.current = selectedPmId;
  const setSelectedPmId = (id: string | null) => setPaymentSelection({ owner: userId, id });
  const quotePromotion = useCartPromotionQuote();
  const promoApplyingRef = useRef(false);
  const quoteRequest = useRef<{ signature: string; id: string } | null>(null);
  function rememberPromotion(value: SavedCartPromotion | null) {
    if (!userId || currentUser.current !== userId) throw new Error('Please sign in again before purchasing.');
    saveCartPromotion(userId, value);
    setSavedPromotion({ owner: userId, value });
  }
  const storedItems = useCart((s) => s.items);
  useEffect(() => {
    const controller = new AbortController();
    paymentCapture.current = controller;
    return () => {
      controller.abort();
      if (paymentCapture.current === controller) paymentCapture.current = null;
    };
  }, [userId, open]);
  const handlePaymentStepEntered = useCallback(() => {
    const controller = paymentCapture.current;
    if (!open || !session || !controller || currentUser.current !== session.user.id) return;
    void capturePaymentStep(session, controller.signal).catch(() => {});
  }, [open, session, storedItems]);
  const labsGate = useLabsExperiment();
  const labs = useLabCartPurchase();
  const labPreview = useLabPreview();
  const items = storedItems.filter((item) => item.origin !== 'lab' || labsGate.enabled || Boolean(labs.attempt));
  const labItems = items.filter((item) => item.origin === 'lab');
  const hasLabs = labItems.length > 0;
  const labCatalog = useLabCatalog(labsGate.enabled && open && hasLabs);
  const [labConsent, setLabConsent] = useState(true);
  useLabView('blood_test_checkout_viewed', 'cart', open && (hasLabs || Boolean(labs.attempt)));
  useEffect(() => { if (!labsGate.loading && !labsGate.enabled && !labs.attempt) storedItems.filter((item) => item.origin === 'lab').forEach((item) => cart.remove(item.id)); }, [labsGate.loading, labsGate.enabled, labs.attempt, hasLabs]);
  useEffect(() => { if (!open) setLabConsent(true); }, [open]);
  const count = items.length;
  const subtotal = items.reduce(
    (acc, i) => acc + i.price_cents * i.quantity,
    0,
  );
  const purchase = useMarketplacePurchase();
  const supplements = useSupplementCartPurchase();
  const supplementItems = items.filter((item) => item.origin === 'supplement');
  const hasSupplements = supplementItems.length > 0;
  // One-time courses are products charged through their own endpoint; every
  // other product line is a subscription plan.
  const courses = useCourseCartPurchase();
  const courseItems = items.filter(isCourseItem);
  const hasCourses = courseItems.length > 0;
  const planItems = items.filter((item) => item.origin === 'product' && !isCourseItem(item));
  const productSubtotal = planItems.reduce((sum, item) => sum + item.price_cents * item.quantity, 0);
  const hasProducts = planItems.length > 0;
  // System Members pay 10% less on treatments, courses included (never labs
  // or supplements); the backend prices the same lines the same way.
  const { percent: memberPercent } = useMembership();
  const memberSavings = (lines: CartItem[]) =>
    lines.reduce(
      (sum, item) =>
        sum + (item.price_cents - memberPriceCents(item.price_cents, memberPercent)) * item.quantity,
      0,
    );
  const planMemberDiscount = memberSavings(planItems);
  const memberDiscount = planMemberDiscount + memberSavings(courseItems);
  const supplementCatalog = useSupplementCatalog(
    open && hasSupplements && supplements.needsPriceReview,
  );
  const [confirming, setConfirming] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const confirmingRef = useRef(false);
  const busy = confirming || purchase.isPending || supplements.busy || labs.busy || courses.busy || quotePromotion.isPending;
  const recoveringPurchase = Boolean(supplements.attempt || labs.attempt || courses.attempt || cartPromotion?.started);
  const locked = busy || recoveringPurchase;
  const labPanels = labCatalog.data?.enabled ? labCatalog.data.items : [];
  const labPricesReady = !hasLabs || Boolean(labCatalog.data?.enabled && labItems.every((item) => {
    const panel = labPanels.find((panel) => panel.product_id === item.id && panel.purchasable);
    return panel && Number.isSafeInteger(panel.subscription_price_cents) && panel.subscription_price_cents > 0 &&
      Number.isSafeInteger(panel.one_time_price_cents) && panel.one_time_price_cents > 0 &&
      item.lab_subscription_price_cents === panel.subscription_price_cents &&
      item.lab_one_time_price_cents === panel.one_time_price_cents &&
      item.price_cents === (item.lab_purchase_type === 'one_time' ? panel.one_time_price_cents : panel.subscription_price_cents);
  }));
  useEffect(() => {
    // The catalog owns current prices. Never rewrite a saved payment's terms.
    if (locked || !labsGate.enabled || !labCatalog.data?.enabled) return;
    for (const item of labItems) {
      const panel = labCatalog.data.items.find((panel) => panel.product_id === item.id && panel.purchasable);
      if (!panel || !Number.isSafeInteger(panel.subscription_price_cents) || panel.subscription_price_cents <= 0 ||
          !Number.isSafeInteger(panel.one_time_price_cents) || panel.one_time_price_cents <= 0) continue;
      if (item.lab_subscription_price_cents !== panel.subscription_price_cents ||
          item.lab_one_time_price_cents !== panel.one_time_price_cents ||
          item.price_cents !== (item.lab_purchase_type === 'one_time' ? panel.one_time_price_cents : panel.subscription_price_cents)) {
        cart.setLabPrices(item.id, panel.subscription_price_cents, panel.one_time_price_cents);
      }
    }
  }, [locked, labsGate.enabled, labCatalog.data, storedItems]);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const lifecycleEvent = useLifecycleEvent();
  // Redeemed referral credit (Stripe account balance) auto-applies to this
  // order's invoice. Surface it here so the discount is visible before paying —
  // Stripe applies up to the invoice total. Only fetched while the cart is open.
  const { data: creditData } = useAccountCredit(open && (hasProducts || labItems.some((item) => item.lab_purchase_type !== 'one_time')));
  const accountCredit = creditData?.account_credit_cents ?? 0;
  // Promo code — validated server-side; the discount previews here and
  // Stripe applies the canonical amount at subscription creation. Credit
  // applies AFTER the coupon (Stripe discounts the invoice first, then
  // draws down the customer balance).
  const [promoCode, setPromoCode] = useState('');
  const [promoMessage, setPromoMessage] = useState<string | null>(null);
  const [appliedPromo, setAppliedPromo] = useState<PromoCodeValidation | null>(
    null,
  );
  // What the applied code was validated for: the plan, or course lines (and
  // whether it may redeem on every one).
  const [coursePromo, setCoursePromo] = useState<{ target: 'plan' | 'course'; reuse: boolean; lines: CoursePromoLine[] }>({ target: 'plan', reuse: false, lines: [] });
  const promoTarget = !hasProducts && hasCourses ? 'course' : 'plan';
  const validatePromo = useValidatePromoCode();
  const validPromotion = cartPromotion && promotionCartMatches(cartPromotion, items, selectedPmId) ? cartPromotion : null;
  const quotedLines = validPromotion ? remainingPromotion(validPromotion, items) : [];
  // A once-per-customer code redeems on one purchase: the plan when the cart
  // has one (courses then go without it), otherwise the first course, or every
  // course when the code allows reuse. Each course line shows exactly the
  // discount the server quoted for its member price.
  const courseMemberCents = (item: CartItem) => memberPriceCents(item.price_cents, memberPercent);
  const promoCourseItems = hasProducts || !appliedPromo?.valid ? [] : coursePromo.reuse ? courseItems : courseItems.slice(0, 1);
  const coursePromoQuoted = promoCourseItems.length > 0 && promoCourseItems.every((item) =>
    coursePromo.lines.some((line) => line.id === item.id && line.amount === courseMemberCents(item)));
  const coursePromoIds = coursePromoQuoted ? promoCourseItems.map((item) => item.id) : [];
  const coursePromoDiscount = coursePromo.lines
    .filter((line) => coursePromoIds.includes(line.id))
    .reduce((sum, line) => sum + line.discount, 0);
  const planPromoDiscount = promoDiscountCents(appliedPromo, productSubtotal);
  const promoDiscount = validPromotion ? quotedLines.reduce((sum, line) => sum + line.discount_cents, 0) : planPromoDiscount + coursePromoDiscount;
  const compoundDiscount = validPromotion ? quotedLines.filter((line) => line.key.startsWith('product:')).reduce((sum, line) => sum + line.discount_cents, 0) : planPromoDiscount;

  const labCreditSubtotal = labItems.filter((item) => item.lab_purchase_type !== 'one_time').reduce((sum, item) => sum + (quotedLines.find((line) => line.key === promotionLineKey(item))?.total_cents ?? item.price_cents), 0);
  // Credit is a Stripe balance on invoices, so one-time course charges never draw it.
  const eligibleCreditSubtotal = Math.max(0, productSubtotal - planMemberDiscount - compoundDiscount) + labCreditSubtotal;
  const creditApplied = Math.min(accountCredit, eligibleCreditSubtotal);
  // Labs are invoiced first. Only the remaining credit can reduce compound revenue.
  const compoundCreditApplied = Math.min(Math.max(0, creditApplied - labCreditSubtotal), Math.max(0, productSubtotal - planMemberDiscount - compoundDiscount));
  const totalDue = Math.max(0, subtotal - memberDiscount - promoDiscount - creditApplied);

  // Apple Pay customers pay in the Apple Pay sheet (a fresh approval while
  // they're here) instead of a background charge on their saved Apple Pay
  // card, which banks decline far more often. Plain plan carts only: labs,
  // supplements, one-time courses (saved card only), saved promotions, dose
  // upgrades and resumed carts keep their own payment paths, and account
  // credit could leave the first charge at $0.
  const paymentMethods = usePaymentMethods(open && (count > 0 || Boolean(courses.attempt)));
  const productLines = planItems;
  const applePayCart =
    paymentMethods.data?.payment_methods.find((pm) => pm.id === selectedPmId)?.wallet === 'apple_pay' &&
    productLines.length > 0 && productLines.length === items.length &&
    !cartPromotion && !labs.attempt && !supplements.attempt && !courses.attempt &&
    creditData !== undefined && creditApplied === 0 &&
    !productLines.some(isUpgradeDoseItem) &&
    !openPurchaseRequest(productLines, selectedPmId, appliedPromo?.code, userId);
  const walletLines = productLines.map((item) => {
    const frequency = item.frequency ?? 'monthly';
    const renewal = renewalCentsFor(item) ?? item.price_cents;
    return {
      title: item.title,
      cadence: frequency,
      firstCents: item.price_cents * item.quantity,
      renewalCents: memberPriceCents(renewal, memberPercent) * item.quantity,
    };
  });
  const walletDiscounts = [
    { label: 'Member discount', cents: memberDiscount },
    { label: `Promo (${appliedPromo?.code})`, cents: promoDiscount },
  ];
  // The cart's arrays are rebuilt every render; rebuild the sheet only when
  // what it shows changes.
  const walletSheetKey = JSON.stringify([walletLines, walletDiscounts, totalDue]);
  const walletSheet = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () => applePaySheet(walletLines, walletDiscounts, totalDue, !appliedPromo?.valid),
    [walletSheetKey, appliedPromo?.valid],
  );
  const wallet = useApplePaySheet({
    enabled: open && applePayCart,
    publishableKey: paymentMethods.data?.publishable_key ?? null,
    sheet: walletSheet,
    onPaymentMethod: (event) => { void handleConfirmPurchase(event); },
  });
  // Stripe's minimum charge is 50¢; below it the saved card is charged.
  const useWallet = wallet.available && totalDue >= 50;
  const walletEventRef = useRef<PaymentRequestPaymentMethodEvent | null>(null);
  /** Close the Apple Pay sheet as soon as the charge is decided. */
  function settleWallet(paid: boolean) {
    const event = walletEventRef.current;
    if (!event) return;
    walletEventRef.current = null;
    event.complete(paid ? 'success' : 'fail');
    wallet.done();
  }

  async function handleApplyPromo() {
    const code = promoCode.trim();
    if (!code || promoApplyingRef.current || validatePromo.isPending || quotePromotion.isPending || cartPromotion?.started) return;
    if (hasLabs && !selectedPmId) { setPromoMessage('Choose your payment method, then apply the code.'); return; }
    promoApplyingRef.current = true;
    const owner = userId;
    const quotedItems = cart.syncFromStorage();
    const paymentMethod = selectedPmId;
    try {
      if (hasLabs) {
        const signature = JSON.stringify([owner, code, paymentMethod, promotionItems(quotedItems)]);
        if (quoteRequest.current?.signature !== signature) quoteRequest.current = { signature, id: crypto.randomUUID() };
        const quote = await quotePromotion.mutateAsync({ request_id: quoteRequest.current.id, code, payment_method_id: paymentMethod!, items: promotionItems(quotedItems) });
        if (currentUser.current !== owner || selectedPmRef.current !== paymentMethod || JSON.stringify(promotionItems(cart.syncFromStorage())) !== JSON.stringify(promotionItems(quotedItems))) return;
        assertPromotionQuote(quote, quotedItems);
        rememberPromotion({ quote, items: quotedItems, paymentMethodId: paymentMethod!, started: false, compoundRequestId: crypto.randomUUID() });
        setAppliedPromo(null);
        setPromoMessage(`${quote.code} applied to your cart.`);
        return;
      }
      // A courses-only cart validates each course line that would take the code.
      const quote = !hasProducts && hasCourses ? await courses.quotePromo(code, courseItems, courseMemberCents) : null;
      const res = quote?.validation ?? await validatePromo.mutateAsync({
        code,
        amount_cents: productSubtotal,
      });
      if (res.valid) {
        setAppliedPromo(res);
        setCoursePromo({ target: quote ? 'course' : 'plan', reuse: quote?.reuse ?? false, lines: quote?.lines ?? [] });
        setPromoMessage(
          `${res.code} applied${res.discount_label ? ` (${res.discount_label})` : ''}.`,
        );
      } else {
        setAppliedPromo(null);
        setPromoMessage(promoErrorCopy(res.reason));
      }
    } catch (cause) {
      if (currentUser.current !== owner) return;
      setAppliedPromo(null);
      if (cause instanceof ApiError && cause.code === 'cart_quote_expired') quoteRequest.current = null;
      if (!cartPromotion?.started) rememberPromotion(null);
      setPromoMessage(cause instanceof Error ? cause.message : 'Could not check that code. Try again in a moment.');
    } finally {
      if (currentUser.current === owner) promoApplyingRef.current = false;
    }
  }

  function handlePromoInput(value: string) {
    if (cartPromotion?.started) return;
    if (cartPromotion) rememberPromotion(null);
    quoteRequest.current = null;
    setPromoCode(value);
    if (appliedPromo) setAppliedPromo(null);
    if (promoMessage) setPromoMessage(null);
  }

  // The drawer never unmounts (it slides via the `open` prop), so promo
  // state would silently survive close → reopen — a code applied for one
  // purchase re-appearing pre-applied on the next. Clear it on close.
  useEffect(() => {
    if (open) return;
    setPromoCode('');
    setAppliedPromo(null);
    setPromoMessage(null);
    if (userId && !cartPromotion?.started) { saveCartPromotion(userId, null); setSavedPromotion({ owner: userId, value: null }); }
  }, [open]);
  const navigate = useNavigate();
  const qc = useQueryClient();
  // The saved card the patient confirmed/picked for this checkout. null until
  // PaymentMethodPicker resolves the default (or the patient has no card).


  useEffect(() => {
    currentUser.current = userId;
    setSavedPromotion({ owner: userId, value: readCartPromotion(userId) });
    setAppliedPromo(null); setPromoCode(''); setPromoMessage(null); quoteRequest.current = null; promoApplyingRef.current = false;
    setPurchaseError(null);
    setReviewError(null);
    setConfirming(false);
    confirmingRef.current = false;
    return () => {
      currentUser.current = undefined;
    };
  }, [userId]);
  useEffect(() => {
    if (!hasSupplements && !supplements.attempt && supplements.needsPriceReview)
      supplements.reviewedPrices();
  }, [hasSupplements, supplements.attempt, supplements.needsPriceReview]);

  useEffect(() => {
    if (!courses.promoRefusal) return;
    setAppliedPromo(null);
    setPromoMessage(promoErrorCopy(courses.promoRefusal));
  }, [courses.promoRefusal]);

  useEffect(() => {
    if (hasLabs && appliedPromo) {
      setAppliedPromo(null);
      setPromoMessage('A blood test was added. Apply the code again to review the discount across your whole cart.');
    } else if (appliedPromo && (coursePromo.target !== promoTarget || (promoCourseItems.length && !coursePromoQuoted))) {
      // The code was validated for another purchase or other prices.
      setAppliedPromo(null);
      setPromoMessage('Your cart changed. Apply the code again to review the discount.');
    }
  }, [hasLabs, appliedPromo, coursePromo.target, promoTarget, promoCourseItems.length, coursePromoQuoted]);

  useEffect(() => {
    if (cartPromotion && !cartPromotion.started && !promotionCartMatches(cartPromotion, items, selectedPmId)) {
      rememberPromotion(null);
      setPromoMessage('Your cart or payment method changed. Apply the code again to review the new total.');
    }
  }, [storedItems, selectedPmId, cartPromotion]);

  function finishPromotionIfPaid() {
    const saved = readCartPromotion(userId);
    if (saved && remainingPromotion(saved, cart.syncFromStorage()).length === 0) rememberPromotion(null);
  }

  // One visible confirmation; supplement lines and compound subscriptions keep
  // their own payment/fulfillment contracts. Never retry a paid line after a
  // partial failure or use a new identity for an uncertain supplement charge.
  function handleConfirmClick() {
    // Apple Pay must open straight from the tap; the purchase runs once the
    // patient approves in the sheet.
    if (useWallet) {
      // A sheet for an older cart never opens: the tap waits for the rebuild.
      if (!busy) wallet.show();
      return;
    }
    void handleConfirmPurchase();
  }

  async function handleConfirmPurchase(walletEvent?: PaymentRequestPaymentMethodEvent) {
    walletEventRef.current = walletEvent ?? null;
    try {
      await confirmPurchase(walletEvent?.paymentMethod.id);
    } finally {
      settleWallet(false);
    }
  }

  async function confirmPurchase(walletPm?: string) {
    if (
      confirmingRef.current || promoApplyingRef.current ||
      (!count && !supplements.attempt && !labs.attempt && !courses.attempt) ||
      (!selectedPmId && !supplements.attempt && !labs.attempt && !courses.attempt)
    )
      return;
    // Saved payments are resolved independently of newly added cart lines.
    if (!recoveringPurchase && supplements.needsPriceReview) return;
    if (supplementItems.length > 10 && !recoveringPurchase) {
      setPurchaseError('You can purchase up to 10 supplement products at a time. Remove a supplement to continue.');
      return;
    }
    if (hasLabs && !recoveringPurchase && (labPreview.stage !== 'actual' || !labsGate.enabled || !labConsent || !labPricesReady)) return;
    confirmingRef.current = true;
    setConfirming(true);
    setPurchaseError(null);
    try {
      if (!userId) throw new Error('Please sign in again before purchasing.');
      const confirm = async () => {
        if (currentUser.current !== userId) return;
        const latestItems = cart.syncFromStorage();
        // Re-read under the shared purchase lock: another tab may have saved
        // a payment since this drawer rendered. A resume never buys new lines.
        if (readLabCartAttempt(userId)) {
          const completed = await labs.purchase([], '');
          if (currentUser.current === userId && completed) finishPromotionIfPaid();
          if (currentUser.current === userId && completed && !cart.subtotalCents()) {
            onClose();
            navigate(labsGate.enabled ? '/app/labs' : '/app/orders');
          }
          return;
        }
        if (readSupplementAttempt(userId)) {
          const completed = await supplements.purchase([], '');
          if (currentUser.current === userId && completed) finishPromotionIfPaid();
          if (currentUser.current === userId && completed && !cart.subtotalCents()) {
            onClose();
            navigate('/app/orders');
          }
          return;
        }
        if (readCourseAttempt(userId)) {
          const paid = await courses.purchase([], '', null, paymentMethods.data?.publishable_key ?? null);
          if (currentUser.current === userId && paid && !cart.subtotalCents()) {
            onClose();
            navigate(postPurchaseDestination(courseIntakeLines(courses.takePaid())));
          }
          return;
        }
        if (
          JSON.stringify(latestItems) !== JSON.stringify(items)
        ) {
          setPurchaseError(
            'Your cart changed in another tab. Review the updated items before confirming.',
          );
          return;
        }
        let promotion = readCartPromotion(userId);
        if (promotion) {
          if (!promotionCartMatches(promotion, latestItems, selectedPmId)) throw new Error('Your cart changed. Review the saved promotion before continuing.');
          promotion = { ...promotion, started: true };
          rememberPromotion(promotion);
        }
        if (hasLabs) {
          const completed = await labs.purchase(labItems, selectedPmId ?? '', promotion);
          if (currentUser.current !== userId || !completed) return;
          if (!hasProducts && !hasSupplements && !hasCourses) {
            if (!cart.subtotalCents()) { finishPromotionIfPaid(); onClose(); navigate('/app/labs'); }
            return;
          }
        }
        if (hasSupplements) {
          const completed = await supplements.purchase(
            supplementItems,
            selectedPmId ?? '',
            promotion,
          );
          if (currentUser.current !== userId || !completed) return;
          if (!hasProducts && !hasCourses) {
            if (!cart.subtotalCents()) {
              finishPromotionIfPaid();
              onClose();
              navigate('/app/orders');
            }
            return;
          }
        }
        // Courses go only to the course endpoint, on the saved card. The code
        // rides only on the course lines quoted for it (never beside a plan).
        let paidCourses = 0;
        if (hasCourses) {
          const paid = await courses.purchase(
            courseItems,
            selectedPmId ?? '',
            !promotion && appliedPromo && coursePromoIds.length ? { code: appliedPromo.code, productIds: coursePromoIds } : null,
            paymentMethods.data?.publishable_key ?? null,
          );
          if (currentUser.current !== userId || !paid) return;
          paidCourses = paid.length;
          if (!hasProducts) {
            if (!cart.subtotalCents()) {
              finishPromotionIfPaid();
              onClose();
              const destination = postPurchaseDestination(courseIntakeLines(courses.takePaid()));
              navigate(hasSupplements && destination === '/app' ? '/app/orders' : destination);
            }
            return;
          }
        }
        if (currentUser.current === userId)
          await purchaseCompounds(promotion, walletPm).catch((cause: unknown) => {
            if (!paidCourses) throw cause;
            const titles = courseItems.map((item) => item.title).join(' and ');
            throw new Error(`${titles} ${courseItems.length === 1 ? 'was' : 'were'} purchased. ${cause instanceof Error ? cause.message : 'The remaining items could not be purchased.'}`);
          });
      };
      if (navigator.locks) {
        await navigator.locks.request(
          `system.portal.purchase.${userId}`,
          confirm,
        );
      } else if (hasSupplements || supplements.attempt || hasLabs || labs.attempt || hasCourses || courses.attempt) {
        throw new Error(
          'Please use an up-to-date browser to confirm this purchase safely.',
        );
      } else {
        await confirm();
      }
    } catch (cause) {
      if (currentUser.current !== userId) return;
      setPurchaseError(
        cause instanceof Error
          ? cause.message
          : 'Purchase failed. Your remaining items are still in the cart.',
      );
    } finally {
      if (currentUser.current === userId) {
        confirmingRef.current = false;
        setConfirming(false);
      }
    }
  }

  // A course that already went through settles as a purchase.
  async function cancelCourse() {
    const paid = await courses.cancel();
    if (currentUser.current === userId && paid && !cart.subtotalCents()) {
      onClose();
      navigate(postPurchaseDestination(courseIntakeLines(courses.takePaid())));
    }
  }

  async function reviewSupplementPrices() {
    setReviewError(null);
    const response = await supplementCatalog.refetch();
    if (currentUser.current !== userId) return;
    if (response.isError || !response.data?.enabled) {
      setReviewError('We could not refresh product prices. Try again shortly.');
      return;
    }
    const unavailable: string[] = [];
    for (const item of supplementItems) {
      const offer = response.data.products.find(
        (product) => product.id === item.id,
      );
      if (!offer || !isPurchasableSupplement(offer))
        unavailable.push(item.title);
      else cart.updateSupplementOffer(item.id, offer.priceCents);
    }
    if (unavailable.length) {
      setReviewError(
        `${unavailable.join(', ')} is unavailable. Remove it from your cart to continue.`,
      );
      return;
    }
    supplements.reviewedPrices();
    setReviewError(
      'Prices updated. Review the total, then confirm your purchase.',
    );
  }

  async function purchaseCompounds(promotion?: SavedCartPromotion | null, walletPm?: string) {
    if (currentUser.current !== userId) return;
    const currentProducts = cart
      .syncFromStorage()
      .filter((item) => item.origin === 'product' && !isCourseItem(item));
    if (
      JSON.stringify(currentProducts) !==
      JSON.stringify(planItems)
    ) {
      throw new Error(
        'Your compound cart changed. Review the remaining items before confirming.',
      );
    }
    const productItems = planItems;
    if (productItems.length === 0) return;
    const productIds = productItems.map((i) => i.id);
    const subtotalDollars = productSubtotal / 100;
    track('checkout_started', {
      source: 'marketplace',
      item_count: productItems.length,
      product_ids: productIds,
      dose_offers: productItems.filter((item) => item.dose_offer_id).map((item) => ({
        id: item.dose_offer_id, version: item.dose_offer_version,
      })),
      price: subtotalDollars,
    });
    // Lifecycle attribution: mark campaign-sourced items as
    // purchase-started so campaign funnels see the conversion attempt.
    productItems
      .filter((i) => i.lifecycle_campaign_key)
      .forEach((item) => {
        lifecycleEvent.mutate({
          campaign_key: item.lifecycle_campaign_key!,
          message_key: item.lifecycle_message_key ?? null,
          recommendation_id: item.lifecycle_recommendation_id ?? null,
          event_type: 'purchase_started',
          metadata: {
            sanity_product_id: item.id,
            slug: item.slug,
          },
        });
      });
    if (promotion) { promotion = { ...promotion, compoundPending: true }; rememberPromotion(promotion); }
    // A dose upgrade (or a quoted promotion) makes the whole compound cart one
    // durable request: every line gets its own receipt, and a retry resumes it.
    const request = promotion
      ? {
          id: promotion.compoundRequestId,
          lines: resumableLines(promotion.items.filter((item) => item.origin === 'product'), promotion.paidProductIds ?? [], productItems),
        }
      : productItems.some(isUpgradeDoseItem)
        ? purchaseRequest(productItems, selectedPmId, appliedPromo?.code, userId)
        : openPurchaseRequest(productItems, selectedPmId, appliedPromo?.code, userId) ?? { id: undefined, lines: productItems };
    const result = await purchase.mutateAsync({
      ...(promotion ? { cart_promotion_id: promotion.quote.id } : {}),
      purchase_request_id: request.id,
      items: request.lines.map((i) => ({
        ...(promotion ? { cart_promotion_line_key: promotionLineKey(i) } : {}),
        sanity_product_id: i.id,
        frequency: i.frequency ?? ('monthly' as const),
        // The default dose goes as the plain product; only an upgrade
        // carries the dose contract.
        ...purchaseDoseSelection(i),
        // Lifecycle campaign attribution (metadata-only) — present when the
        // item was added via a campaign email deep link.
        ...(i.lifecycle_campaign_key
          ? {
              lifecycle_campaign_key: i.lifecycle_campaign_key,
              lifecycle_message_key: i.lifecycle_message_key,
              lifecycle_recommendation_id: i.lifecycle_recommendation_id,
            }
          : {}),
      })),
      // Charge the card the patient confirmed/picked (or the Apple Pay sheet
      // they just approved). Omitted when null so the backend falls back to
      // the customer's default card.
      ...(walletPm ? { wallet_payment_method_id: walletPm } : selectedPmId ? { payment_method_id: selectedPmId } : {}),
      ...(!promotion && appliedPromo?.valid ? { promo_code: appliedPromo.code } : {}),
    }).catch(async (cause: unknown) => {
      if (currentUser.current !== userId) throw cause;
      const closed = isClosedPurchaseAttempt(cause);
      // Lines charged before a later line failed are bought: take them out of
      // the cart now. A retry of the rest resumes the same request.
      const paid = paidPurchaseLines(cause);
      const paidIds = paid.map((line) => line.sanity_product_id);
      // The sheet's card paid the first line(s); the rest failed afterwards.
      if (paid.length) settleWallet(true);
      if (!promotion && request.id) {
        if (closed) retirePurchaseRequest();
        else recordPaidLines(request.id, paidIds);
      }
      // A closed promoted request cannot be reused: its quoted lines stay
      // bound to it. The remaining items continue at their regular price.
      const promotionEnded = Boolean(promotion && closed);
      if (promotion && closed) {
        // Keep the card the quote was using selected; the patient can change it.
        setSelectedPmId(promotion.paymentMethodId);
        rememberPromotion(null);
      } else if (promotion && paidIds.length) {
        rememberPromotion({
          ...promotion,
          paidProductIds: [...new Set([...(promotion.paidProductIds ?? []), ...paidIds])],
        });
      }
      const promotionNote = promotionEnded && cart.syncFromStorage().some((item) => !paidIds.includes(item.id))
        ? ' The promotion no longer applies to the remaining items. Review the total before purchasing.'
        : '';
      if (!paid.length) {
        if (!promotionNote) throw cause;
        throw new Error(`${cause instanceof Error ? cause.message : 'The purchase could not be completed.'}${promotionNote}`);
      }
      const titles = productItems.filter((item) => paidIds.includes(item.id)).map((item) => item.title);
      paidIds.forEach((id) => cart.remove(id));
      qc.setQueryData<MarketplaceResponse>(['marketplace'], (current) =>
        current ? marketplaceAfterPurchase(current, paid.map((line) => line.canonical_product_id)) : current,
      );
      // The paid lines settled like a successful purchase: refresh the same
      // ownership, Home, clinical and credit views.
      await Promise.allSettled([
        qc.invalidateQueries({ queryKey: ['marketplace'], refetchType: 'all' }),
        qc.invalidateQueries({ queryKey: ['me', 'treatment-offers'] }),
        qc.invalidateQueries({ queryKey: ['me', 'required-intakes'] }),
        qc.invalidateQueries({ queryKey: ['me', 'clinical-state'] }),
        qc.invalidateQueries({ queryKey: ['me', 'orders'] }),
        qc.invalidateQueries({ queryKey: ['home'] }),
        qc.invalidateQueries({ queryKey: ['me', 'subscriptions'] }),
        qc.invalidateQueries({ queryKey: ['me', 'account-credit'] }),
      ]);
      const message = cause instanceof Error ? cause.message : 'The remaining items could not be purchased.';
      throw new Error(
        (titles.length
          ? `${titles.join(' and ')} ${titles.length === 1 ? 'was' : 'were'} purchased. ${message}`
          : message) + promotionNote,
      );
    });
    settleWallet(true);
    if (currentUser.current !== userId) return;
    retirePurchaseRequest();
    // Funnel-completion event — mirrors system-web's `complete_purchase`
    // (source: 'checkout') and the upsell overlay's `complete_purchase`
    // (source: 'upsell') so the same revenue dashboard captures all three
    // purchase surfaces. Placed AFTER mutateAsync resolves so a failed
    // API call (mutateAsync throws → React Query owns the error state)
    // never registers as completed revenue. `track()` wraps the capture
    // in try/catch internally, so even a PostHog SDK fault here can't
    // break the cart-clear + navigate that follows.
    const cartFreqs = [
      ...new Set(productItems.map((i) => i.frequency ?? 'monthly')),
    ];
    track('complete_purchase', {
      source: 'marketplace',
      products: productIds,
      product_count: productItems.length,
      success_count: result?.items?.length ?? 0,
      // Discounted amount actually charged (promo −, credit −) so the
      // revenue dashboards don't overstate GMV on couponed orders.
      price: (productSubtotal - planMemberDiscount - compoundDiscount - compoundCreditApplied) / 100,
      // Parity with checkout's revenue schema: amount_paid in dollars, and
      // a single filterable plan-length `duration` ('mixed' when items
      // disagree) alongside the per-item `frequencies` array.
      amount_paid: (productSubtotal - planMemberDiscount - compoundDiscount - compoundCreditApplied) / 100,
      duration: cartFreqs.length === 1 ? cartFreqs[0] : 'mixed',
      promo_code: appliedPromo?.valid ? appliedPromo.code : undefined,
      currency: 'USD',
      frequencies: productItems.map((i) => i.frequency ?? 'monthly'),
      dose_offers: productItems.filter((item) => item.dose_offer_id).map((item) => ({
        id: item.dose_offer_id, version: item.dose_offer_version,
      })),
      // Stack attribution — which Protocols-tab stacks drove items in
      // this purchase (shop-stacks-v1 experiment readout).
      stacks: [
        ...new Set(productItems.map((i) => i.stack_slug).filter(Boolean)),
      ],
    });
    const purchasedCanonicalIds = result.items.map(
      (item) => item.canonical_product_id,
    );
    qc.setQueryData<MarketplaceResponse>(['marketplace'], (current) =>
      current
        ? marketplaceAfterPurchase(current, purchasedCanonicalIds)
        : current,
    );
    productItems.forEach((item) => cart.remove(item.id));
    finishPromotionIfPaid();
    // Refetch ownership before Home can reuse the five-minute marketplace
    // cache. allSettled is deliberate: the charge already succeeded, so an
    // unrelated refresh failure must never turn the completed purchase into
    // a misleading "Purchase failed" state.
    await Promise.allSettled([
      qc.invalidateQueries({ queryKey: ['marketplace'], refetchType: 'all' }),
      qc.invalidateQueries({ queryKey: ['me', 'required-intakes'] }),
      qc.invalidateQueries({ queryKey: ['me', 'clinical-state'] }),
      qc.invalidateQueries({ queryKey: ['me', 'orders'] }),
      qc.invalidateQueries({ queryKey: ['home'] }),
      qc.invalidateQueries({ queryKey: ['me', 'subscriptions'] }),
      qc.invalidateQueries({ queryKey: ['me', 'account-credit'] }),
    ]);
    if (currentUser.current !== userId) return;
    onClose();
    // Courses paid in this or an earlier, partly failed confirmation.
    const destination = postPurchaseDestination([...courseIntakeLines(courses.takePaid()), ...result.items]);
    navigate(
      hasSupplements && destination === '/app' ? '/app/orders' : destination,
    );
  }

  return (
    <>
      {open && (
        <button
          type="button"
          aria-label="Close cart"
          onClick={onClose}
          className="fixed inset-0 bg-black/10 backdrop-blur-[10px] z-helpdock"
        />
      )}
      <aside
        aria-hidden={!open}
        inert={!open}
        aria-label="Cart"
        // Mobile: full-bleed below the SearchBar all the way to the bottom
        // (covers the BottomNav when open). Desktop: right rail. When closed
        // we slide off-screen AND set pointer-events-none so the off-screen
        // panel doesn't intercept clicks. Previously `bottom-50` constrained
        // the panel's height so translate-y-full only hid the body — the
        // header band kept peeking up at the bottom of every page.
        className={cn(
          'fixed inset-x-0 top-45 bottom-0 md:top-0 md:right-0 md:left-auto md:w-[420px] z-drawer',
          'bg-[var(--surface-page)] border-l border-line-subtle flex flex-col',
          'transition-transform duration-base ease-ease',
          open
            ? 'translate-y-0 md:translate-x-0'
            : 'translate-y-full md:translate-y-0 md:translate-x-full pointer-events-none',
        )}
      >
        <header className="flex items-center justify-between bg-[var(--surface-sunken)] border-b border-line-subtle">
          <h2 className="font-mono uppercase text-13 text-ink-secondary px-15 py-15">
            {hasSupplements || supplements.attempt || hasCourses || courses.attempt ? 'CART' : 'PLAN'} [
            {String(count).padStart(2, '0')}]
          </h2>
          <button
            ref={closeButton}
            type="button"
            onClick={onClose}
            aria-label="Close cart"
            className="w-45 h-45 flex items-center justify-center border-l border-line-subtle bg-slate text-ink-secondary hover:text-ink"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </header>

        {count === 0 && !supplements.attempt && !labs.attempt && !courses.attempt ? (
          <>
            {/* A refusal can take the last course out of the cart; keep its
                reason in view. */}
            {courses.error && (
              <div role="alert" className="type-body-sm text-danger px-15 pt-15">
                {courses.error}
              </div>
            )}
            <EmptyState onClose={onClose} />
          </>
        ) : (
          <>
            <ul className="flex-1 overflow-y-auto min-h-0">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="flex items-stretch border-b border-line-subtle"
                >
                  <div className="w-[100px] shrink-0 bg-[var(--surface-sunken)] aspect-square overflow-hidden">
                    {item.image_url ? (
                      // eslint-disable-next-line jsx-a11y/img-redundant-alt
                      <img
                        src={item.image_url}
                        alt={item.title}
                        className={cn(
                          'w-full h-full',
                          ['supplement', 'lab'].includes(item.origin)
                            ? 'object-contain p-8'
                            : 'object-cover',
                        )}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg
                          viewBox="0 0 64 80"
                          width="40"
                          height="50"
                          fill="none"
                          aria-hidden
                          className="text-ink-muted"
                        >
                          <rect
                            x="22"
                            y="2"
                            width="20"
                            height="8"
                            rx="2"
                            stroke="currentColor"
                            strokeWidth="1.4"
                          />
                          <rect
                            x="14"
                            y="10"
                            width="36"
                            height="68"
                            rx="3"
                            stroke="currentColor"
                            strokeWidth="1.4"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex items-start justify-between gap-15 px-15 pt-12">
                      <div className="min-w-0">
                        <div className="type-title-sm text-ink truncate">
                          {item.title.toUpperCase()}
                        </div>
                        {item.subtitle && (
                          <div className="type-body-sm text-ink-secondary mt-3">
                            {item.subtitle}
                          </div>
                        )}
                      </div>
                      <div className="type-data-md text-ink shrink-0">
                        {formatPrice(item.price_cents * item.quantity)}
                      </div>
                    </div>
                    {item.origin === 'lab' ? (
                      <div className="px-15 py-12"><LabPurchaseOptions disabled={locked} purchaseType={item.lab_purchase_type ?? 'subscription'} interval={item.lab_interval_months ?? 1} onChange={(type, interval) => { cart.setLabOptions(item.id, type, interval); }} />
                        <p className="type-body-sm text-ink-secondary mt-10">{labBillingCopy(item, quotedLines.find((line) => line.key === promotionLineKey(item)))}</p>
                      </div>
                    ) : item.origin === 'supplement' ? (
                      <div className="px-15 py-10 flex items-center justify-between gap-10">
                        <span className="type-label-xs text-ink-muted">
                          One-time
                        </span>
                        <label className="flex items-center gap-8 type-body-sm">
                          Quantity
                          <select
                            aria-label={`Quantity for ${item.title}`}
                            value={item.quantity}
                            disabled={locked}
                            onChange={(event) =>
                              cart.setQuantity(
                                item.id,
                                Number(event.target.value),
                              )
                            }
                            className="h-44 px-10 border border-line-subtle bg-page disabled:opacity-60"
                          >
                            {[1, 2, 3].map((quantity) => (
                              <option key={quantity} value={quantity}>
                                {quantity}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    ) : isCourseItem(item) ? (
                      <div className="px-15 py-10">
                        <span className="type-label-xs text-ink-muted">
                          One-time course · single charge · no subscription
                        </span>
                      </div>
                    ) : (
                      <fieldset disabled={locked}>
                        <PlanLengthSelector item={item} />
                        <RenewalDisclosure item={item} />
                      </fieldset>
                    )}
                    <button
                      type="button"
                      disabled={locked}
                      onClick={() => cart.remove(item.id)}
                      className="mt-auto flex items-center justify-between px-15 py-8 type-body-sm text-ink-secondary bg-[var(--surface-sunken)] border-t border-line-subtle hover:text-ink"
                    >
                      Remove
                      <X size={14} strokeWidth={2} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            {/*
              min-h-0 + overflow-y-auto: the footer hosts the payment picker,
              which expands to mount Stripe's PaymentElement + Link "save info"
              block when adding a card. That can exceed the panel height; without
              an internal scroll the footer's bottom (Save card / Confirm) spills
              off-screen with no way to reach it (caught in Firefox on staging).
              Letting the footer shrink + scroll keeps every button reachable;
              the short non-add case is unaffected (no scrollbar appears).
            */}
            <footer className="border-t border-line-subtle bg-[var(--surface-page)] px-15 py-15 min-h-0 overflow-y-auto">
              {hasLabs && !recoveringPurchase && !labPricesReady && <p role="status" className="type-body-sm text-ink-secondary mb-10">{labCatalog.isFetching ? 'Checking blood test pricing…' : 'Blood test pricing is not available yet. '} {!labCatalog.isFetching && <button className="underline min-h-44" onClick={() => void labCatalog.refetch()}>Try again</button>}</p>}
              {(purchaseError || supplements.error || labs.error || courses.error) && (
                <div role="alert" className="type-body-sm text-danger mb-10">
                  {purchaseError || supplements.error || labs.error || courses.error}
                  {!purchaseError && !supplements.error && labs.errorCode === 'lab_profile_incomplete' && (
                    <>
                      {' '}
                      <Link className="underline" to="/app/account?focus=profile" onClick={onClose}>
                        Complete your profile
                      </Link>
                    </>
                  )}
                </div>
              )}
              {/* Promo code — validated server-side; discount previews
                  below and Stripe applies the canonical amount at
                  subscription creation. */}
              {(hasProducts || hasCourses || hasLabs || Boolean(cartPromotion)) && (
                <fieldset disabled={locked}>
                  <div className="flex items-center gap-8 mb-6">
                    <input
                      type="text"
                      placeholder="Promo code"
                      aria-label="Promo code"
                      value={cartPromotion?.quote.code ?? promoCode}
                      onChange={(e) => handlePromoInput(e.target.value)}
                      disabled={purchase.isPending}
                      className="flex-1 min-w-0 border border-line-subtle bg-transparent px-10 py-8 type-body-sm text-ink placeholder:text-ink-muted outline-none disabled:opacity-60"
                    />
                    <button
                      type="button"
                      onClick={handleApplyPromo}
                      disabled={
                        purchase.isPending ||
                        validatePromo.isPending || quotePromotion.isPending ||
                        !promoCode.trim()
                      }
                      className="shrink-0 border border-line-subtle px-12 py-8 type-label-sm text-ink hover:bg-[var(--surface-sunken)] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {validatePromo.isPending || quotePromotion.isPending ? 'CHECKING…' : 'APPLY'}
                    </button>
                  </div>
                  {promoMessage && (
                    <div
                      className={cn(
                        'type-body-sm mb-10',
                        appliedPromo || validPromotion ? 'text-status-success' : 'text-ink-muted',
                      )}
                    >
                      {promoMessage}
                    </div>
                  )}
                  {hasSupplements && !hasLabs && !cartPromotion && (
                    <p className="type-body-sm text-ink-muted mb-10">
                      Promo codes and account credit apply to compounds.
                    </p>
                  )}
                  {hasCourses && hasProducts && !hasLabs && (
                    <p className="type-body-sm text-ink-muted mb-10">
                      Promo codes and account credit apply to your plan, not the one-time course.
                    </p>
                  )}
                </fieldset>
              )}
              {hasSupplements && (
                <p className="type-body-sm text-ink-secondary mb-12">
                  Supplements are one-time purchases.
                  Shipping and tax included. Delivered to your account's
                  shipping address.{' '}
                  <Link
                    to="/app/account"
                    onClick={onClose}
                    className="underline"
                  >
                    Review address
                  </Link>
                </p>
              )}
              {(hasLabs || labs.attempt) && <div className="type-body-sm text-ink-secondary mb-12">
                <p>Blood tests use the details and shipping address saved in your account. <Link className="underline" to="/app/account" onClick={onClose}>Review details</Link></p>
                {!labs.attempt && <label className="flex gap-10 mt-12 leading-145"><input type="checkbox" aria-label="Consent to blood testing and selected billing schedule" checked={labConsent} disabled={busy} onChange={(event) => setLabConsent(event.target.checked)} className="mt-3 shrink-0"/><span>I consent to at-home collection, laboratory testing, and sharing my results with my System care team. I agree to the purchase and billing schedule shown above.</span></label>}
                {labs.attempt && <><p role="status" className="mt-10">A saved blood test payment needs to be checked before changing your cart.</p><button type="button" disabled={busy} onClick={() => void labs.cancel()} className="underline min-h-44">Cancel unpaid blood test purchase</button></>}
                {labPreview.stage !== 'actual' && <p role="status" className="mt-8">Exit staging preview in Labs before purchasing.</p>}
              </div>}
              {supplements.attempt && (
                <div
                  className="type-body-sm text-ink-secondary mb-12"
                  role="status"
                >
                  A supplement payment needs to be checked before changing your
                  cart.
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void supplements.cancel()}
                    className="underline block min-h-44"
                  >
                    Cancel unpaid purchase
                  </button>
                </div>
              )}
              {courses.attempt && (
                <div
                  className="type-body-sm text-ink-secondary mb-12"
                  role="status"
                >
                  A course payment needs to be checked before changing your
                  cart.
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void cancelCourse()}
                    className="underline block min-h-44"
                  >
                    Cancel unpaid course purchase
                  </button>
                </div>
              )}
              {supplements.needsPriceReview && (
                <button
                  type="button"
                  disabled={busy || supplementCatalog.isFetching}
                  onClick={() => void reviewSupplementPrices()}
                  className="underline min-h-44 type-body-sm"
                >
                  Review updated prices
                </button>
              )}
              {reviewError && (
                <p role="status" className="type-body-sm mb-10">
                  {reviewError}
                </p>
              )}
              <div className="flex items-baseline justify-between mb-12">
                <span className="type-label-sm text-ink-muted">SUBTOTAL</span>
                <span className="type-data-lg text-ink">
                  {formatPrice(subtotal)}
                </span>
              </div>
              {memberDiscount > 0 && (
                <div className="flex items-baseline justify-between mb-12">
                  <span className="type-label-sm text-ink-muted">
                    MEMBER DISCOUNT ({memberPercent}%)
                  </span>
                  <span className="type-data-lg text-status-success">
                    −{formatPrice(memberDiscount)}
                  </span>
                </div>
              )}
              {promoDiscount > 0 && (
                <div className="flex items-baseline justify-between mb-12">
                  <span className="type-label-sm text-ink-muted">
                    PROMO ({validPromotion?.quote.code ?? appliedPromo?.code})
                  </span>
                  <span className="type-data-lg text-status-success">
                    −{formatPrice(promoDiscount)}
                  </span>
                </div>
              )}
              {creditApplied > 0 && (
                <div className="flex items-baseline justify-between mb-12">
                  <span className="type-label-sm text-ink-muted">
                    ACCOUNT CREDIT
                  </span>
                  <span className="type-data-lg text-status-success">
                    −{formatPrice(creditApplied)}
                  </span>
                </div>
              )}
              {(creditApplied > 0 || promoDiscount > 0 || memberDiscount > 0) && (
                <div className="flex items-baseline justify-between mb-12 pt-12 border-t border-line-subtle">
                  <span className="type-label-sm text-ink">TOTAL DUE</span>
                  <span className="type-data-lg text-ink">
                    {formatPrice(totalDue)}
                  </span>
                </div>
              )}
              {cartPromotion?.started && !cartPromotion.compoundPending && !labs.attempt && !supplements.attempt && <div className="type-body-sm text-ink-secondary mb-12"><p>Already paid items have been removed. The remaining items keep their original promotion and saved payment method.</p><button type="button" disabled={busy} className="min-h-44 underline" onClick={() => { const saved = readCartPromotion(userId); if (!saved) return; saved.items.forEach((item) => cart.remove(item.id)); rememberPromotion(null); setPromoCode(''); setPromoMessage(null); }}>Clear remaining items</button></div>}
              <fieldset disabled={locked}>
                <PaymentMethodPicker
                  key={userId}
                  selectedPmId={selectedPmId}
                  onSelect={setSelectedPmId}
                  enabled={open && count > 0}
                  onPaymentStepEntered={handlePaymentStepEntered}
                  paymentEntryKey={paymentStepKey(userId, storedItems)}
                />
              </fieldset>
              <button
                type="button"
                onClick={handleConfirmClick}
                disabled={
                  busy ||
                  (hasLabs && !recoveringPurchase && (labPreview.stage !== 'actual' || !labConsent || !labsGate.enabled || !labPricesReady)) ||
                  (!selectedPmId && !supplements.attempt && !labs.attempt && !courses.attempt) ||
                  (!recoveringPurchase && supplements.needsPriceReview)
                }
                className="w-full flex items-center justify-between h-50 px-15 bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] type-body-md hover:opacity-90 disabled:opacity-60"
              >
                {busy ? (
                  <>
                    <span className="flex items-center gap-10">
                      <Loader2 size={14} className="animate-spin" /> Confirming…
                    </span>
                    <span
                      aria-hidden
                      className="inline-flex items-center justify-center w-8 h-10"
                    >
                      <BrandIcon name="Arrow" viewBox="0 0 10 12" />
                    </span>
                  </>
                ) : (
                  <>
                    {labs.attempt ? 'Resume blood test payment' : cartPromotion?.started ? 'Continue remaining purchase' : supplements.attempt || courses.attempt
                      ? 'Check purchase status'
                      : useWallet ? 'Confirm with \uF8FF Pay' : 'Confirm Purchase'}
                    <span
                      aria-hidden
                      className="inline-flex items-center justify-center w-8 h-10"
                    >
                      <BrandIcon name="Arrow" viewBox="0 0 10 12" />
                    </span>
                  </>
                )}
              </button>
              {(hasProducts || hasCourses) && !recoveringPurchase && <CarePurchaseNote />}
            </footer>
          </>
        )}
      </aside>
    </>
  );
}

/** Per-item plan-length picker. Hidden for items that only price monthly
 *  (no selector to show). Changing the term reprices the row + subtotal
 *  (cart.setFrequency keeps price_cents in lockstep) and flows through to
 *  the purchase payload's per-item `frequency`.
 *
 *  A native <select> instead of segmented buttons: the buttons wrapped to
 *  a second row that sat flush against the Remove strip — an easy mis-tap
 *  on mobile. One control, one row, clear air before Remove. */
function PlanLengthSelector({ item }: { item: CartItem }) {
  const terms = offerableFrequencies(item);
  if (terms.length < 2) return null;
  const active = item.frequency ?? 'monthly';
  return (
    <div className="px-15 pt-8 pb-14">
        <PlanSelect
          aria-label="Plan length"
          value={active}
          onChange={(e) =>
            cart.setFrequency(item.id, e.target.value as PlanFrequency)
          }
        >
          {terms.map((f) => (
            <option key={f} value={f}>
              {FREQUENCY_LABELS[f]} | $
              {((item.plan_prices?.[f] ?? 0) / 100).toFixed((item.plan_prices?.[f] ?? 0) % 100 ? 2 : 0)}
            </option>
          ))}
        </PlanSelect>
    </div>
  );
}

/** "$99 first month, renews at $179/mo" — the intro price alone reads
 *  as the ongoing price, which it isn't. Tracks the selected term. */
function RenewalDisclosure({ item }: { item: CartItem }) {
  const listRenewal = renewalCentsFor(item);
  // Members renew at the member price on treatments (the backend attaches
  // the member coupon to the plan); labs and supplements never change.
  const { percent: memberPercent } = useMembership();
  const memberRenewal = memberPercent > 0 && item.origin === 'product';
  const renewal =
    listRenewal != null && memberRenewal
      ? memberPriceCents(listRenewal, memberPercent)
      : listRenewal;
  if (renewal == null) return null;
  // Today's member charge, matching the cart's member discount line.
  const first = memberRenewal
    ? memberPriceCents(item.price_cents, memberPercent)
    : item.price_cents;
  const freq = item.frequency ?? 'monthly';
  const firstLabel =
    freq === 'monthly'
      ? 'first month'
      : `first ${FREQUENCY_LABELS[freq].toLowerCase()} supply`;
  return (
    <p className="px-15 pb-10 -mt-6 type-label-xs text-ink-muted">
      {formatPrice(first)} {firstLabel} | renews at{' '}
      {formatPrice(renewal)}
      {FREQUENCY_CYCLE_LABELS[freq]}
      {memberRenewal ? ' member price' : ''}
    </p>
  );
}

function EmptyState({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-15 py-40 gap-20">
      <div className="type-label-md text-ink-muted">CART EMPTY</div>
      <p className="type-body-md text-ink-secondary max-w-[32ch]">
        Add compounds, stacks, or supplements from the Shop to get started.
      </p>
      <Link
        to="/app/shop"
        onClick={onClose}
        className="w-full max-w-[28rem] flex items-center justify-between h-50 px-15 bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] type-body-md hover:opacity-90"
      >
        Shop
        <span
          aria-hidden
          className="inline-flex items-center justify-center w-8 h-10"
        >
          <BrandIcon name="Arrow" viewBox="0 0 10 12" />
        </span>
      </Link>
    </div>
  );
}
